import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import clsx from "clsx";

import { dataTutorials } from "@data/index";
import { dataFaq } from "@data/index";
import { localStorageService } from "@services/index";

import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  TableTabs,
  PlayerPlaySvg,
  ChevronDownSvg,
} from "@components/index";

export const HelpPage = ({
  tutorialNames,
  faqCategory = [],
  onClose,
}: {
  tutorialNames?: string[];
  faqCategory: string[];
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    tutorialNames?.length ? "Tutorials" : "FAQ: How to",
  );
  const [expandedItem, setExpandedItem] = useState("none");

  const faq = useMemo(
    () => dataFaq.filter(({ category }) => faqCategory.includes(category)),
    [faqCategory],
  );

  useEffect(() => {
    if (faq.length) {
      setExpandedItem(`${faq[0].category}-0`);
    }
  }, [faq]);

  const handleStartTutorial = (name: string) => {
    onClose();
    localStorageService.setTutorialName(name);
    navigate(0);
  };

  return (
    <Drawer className="drawer-help-page">
      <DrawerHeader
        onClose={onClose}
        className="text-300 font-bold md:text-600"
        title="Help"
      />
      <DrawerScrollableContainer
        className={clsx(
          tutorialNames?.length ? "pt-5 md:pt-7" : "pt-8 md:pt-10",
        )}
      >
        {!!tutorialNames?.length && (
          <TableTabs
            tabs={["Tutorials", "FAQ: How to"]}
            activeTab={activeTab}
            onClick={(value) => setActiveTab(value)}
            className="border-b border-b-black-8"
            classnameItem="w-full md:w-auto"
          />
        )}
        {activeTab === "Tutorials" && tutorialNames?.length && (
          <div className="flex flex-col gap-y-4 py-8">
            {tutorialNames.map((name) => {
              const tutorial = dataTutorials.find((t) => t.name === name);

              return (
                <div
                  key={name}
                  className="rounded-lg border border-black-8 px-6 py-4"
                >
                  <div className="flex justify-between">
                    <div className="text-300 font-bold">
                      {tutorial?.label || name}
                    </div>
                    <button
                      className="flex items-center gap-x-2 text-200 font-bold text-primary-100"
                      onClick={() => handleStartTutorial(name)}
                    >
                      Start <PlayerPlaySvg />
                    </button>
                  </div>
                  <div className="pt-4 text-300 font-light text-black-70">
                    {tutorial?.description}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {activeTab === "FAQ: How to" && (
          <div className="flex flex-col gap-y-6 py-6">
            {faq.map(({ category, videoLink, items }) => (
              <div key={category}>
                <div className="flex flex-col justify-between sm:flex-row sm:items-center">
                  <div className="text-400 font-bold">{category}</div>
                  {videoLink && (
                    <Link
                      to={videoLink}
                      className="text-300 font-light text-primary-300 underline hover:no-underline"
                      target="_blank"
                    >
                      Watch the video
                    </Link>
                  )}
                </div>
                <div className="flex flex-col gap-y-4 py-4">
                  {items.map((item, index) => (
                    <div
                      key={`${category}-${index}`}
                      className="rounded-lg bg-black-4 p-6"
                    >
                      <div
                        className="flex cursor-pointer items-center justify-between text-300 font-bold"
                        onClick={() =>
                          setExpandedItem(
                            `${category}-${index}` === expandedItem
                              ? "none"
                              : `${category}-${index}`,
                          )
                        }
                      >
                        <div>{item.title}</div>
                        <div
                          className={clsx(
                            "ml-2 inline-block text-black-54",
                            expandedItem === `${category}-${index}` &&
                              "rotate-180",
                          )}
                        >
                          <ChevronDownSvg />
                        </div>
                      </div>
                      {expandedItem === `${category}-${index}` && (
                        <div className="pt-4 text-300 font-light text-black-54 [&_a]:text-primary-300 [&_a]:underline">
                          {item.content}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DrawerScrollableContainer>
    </Drawer>
  );
};
